import { ShaderProgram } from '../renderer/ShaderProgram';
import { InstancedMesh } from '../renderer/InstancedMesh';
import type { WorldConfig } from '../config/WorldConfig';
import type { Camera } from '../renderer/Camera';
import type { Renderable } from '../renderer/RenderPipeline';
import type { ClusterManager } from '../world/ClusterManager';

import vertSrc from '../shaders/hex-grid.vert.glsl?raw';
import fragSrc from '../shaders/hex-grid.frag.glsl?raw';

const MAX_VISIBLE_HEXES = 50_000;
const FLOATS_PER_INSTANCE = 5;
const _mergedBuffer = new Float32Array(MAX_VISIBLE_HEXES * FLOATS_PER_INSTANCE);

/**
 * Renders hex outlines via instanced drawing.
 * Each hex is a 7-vertex hexagon (center + 6 corners).
 * Alpha is kept low so shared edges blend smoothly.
 *
 * Frustum culling: only clusters within camera view distance are merged.
 */
export class HexTileRenderer implements Renderable {
  private _shader: ShaderProgram;
  private _mesh: InstancedMesh;
  private _hexRadius: number;
  private _currentHexCount = 0;
  private _alpha = 0.55;
  private _alphaTarget = 0.55;

  constructor(gl: WebGL2RenderingContext, config: WorldConfig) {
    this._hexRadius = config.hexRadius;
    this._shader = new ShaderProgram(gl, vertSrc, fragSrc);

    this._mesh = new InstancedMesh(
      gl,
      this._shader,
      buildHexGeometry(),
      [{ name: 'a_corner', size: 2, divisor: 0 }],
      [
        { name: 'a_offset', size: 2, divisor: 1 },
        { name: 'a_color', size: 3, divisor: 1 },
      ],
      buildHexIndices(),
    );
  }

  /**
   * Merge cluster data with distance-based frustum culling.
   * Only clusters whose center is within viewable range of the
   * camera target get merged into the GPU buffer.
   */
  rebuildFromManager(manager: ClusterManager, camera: Camera): void {
    const tx = camera.target.x;
    const tz = camera.target.z;
    const zoom = camera.position.y;
    const maxDist = Math.max(zoom * 5, 200);
    const maxDistSq = maxDist * maxDist;

    let offset = 0;
    let totalHexes = 0;

    manager.forEachCluster((cluster) => {
      const cx = (cluster.boundsMinX + cluster.boundsMaxX) * 0.5;
      const cz = (cluster.boundsMinZ + cluster.boundsMaxZ) * 0.5;
      const dx = cx - tx;
      const dz = cz - tz;

      if (dx * dx + dz * dz > maxDistSq) return;

      const needed = cluster.hexCount * FLOATS_PER_INSTANCE;
      if (offset + needed > _mergedBuffer.length) return;

      _mergedBuffer.set(cluster.instanceData, offset);
      offset += needed;
      totalHexes += cluster.hexCount;
    });

    this._currentHexCount = totalHexes;
    this._mesh.updateInstances(
      _mergedBuffer.subarray(0, offset),
      totalHexes,
    );
  }

  get hexCount(): number { return this._currentHexCount; }

  set alpha(v: number) { this._alphaTarget = v; }

  lerpAlpha(dt: number, speed: number): void {
    const diff = this._alphaTarget - this._alpha;
    if (Math.abs(diff) < 0.001) { this._alpha = this._alphaTarget; return; }
    this._alpha += diff * Math.min(1, dt * speed * 3);
  }

  render(_gl: WebGL2RenderingContext, camera: Camera): void {
    if (this._currentHexCount === 0) return;

    this._shader.use();
    this._shader.setMat4('u_projection', camera.projection.data);
    this._shader.setMat4('u_view', camera.view.data);
    this._shader.setFloat('u_hexRadius', this._hexRadius);
    this._shader.setFloat('u_alpha', this._alpha);
    this._mesh.draw();
  }

  dispose(): void {
    this._shader.dispose();
    this._mesh.dispose();
  }
}

function buildHexGeometry(): Float32Array {
  const verts = new Float32Array(14);
  verts[0] = 0; verts[1] = 0;
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i;
    verts[(i + 1) * 2] = Math.cos(angle);
    verts[(i + 1) * 2 + 1] = Math.sin(angle);
  }
  return verts;
}

function buildHexIndices(): Uint16Array {
  return new Uint16Array([
    0, 1, 2,  0, 2, 3,  0, 3, 4,
    0, 4, 5,  0, 5, 6,  0, 6, 1,
  ]);
}
